import { type Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb';
import { FilterExpression } from '@pushwoosh/rpc-service-client/pushwoosh/objects/filters/v1/model_pb';
import type {
  SegmentCondition,
  ConditionOperator,
  SegmentConditionOperatorSingle,
  ConditionCriterionCommon,
  SegmentGroupOperator,
  SegmentConditionEvent,
  SingleCondition,
  ConditionClause,
  NamedConditionCriterion
} from './types';
import {
  type OperatorConfig,
  type ValueTypeConfig,
  operators,
  valuesTypes,
} from './common';
import OperationOperator = FilterExpression.Operation.Operator;
import EventCountOperator = FilterExpression.Event.Count.Operator;
import { formatDateToUTCString } from './helpers';


function parseOperators(
  typeObj: any,
  convertFromDb: (v: any) => any = (v) => v,
): ConditionClause {
  const opConf = operators.find((op) => {
    // hasEq | hasBetween ...
    const hasMethodName = `has${op.method}`;
    return typeObj[hasMethodName]?.();
  }) as OperatorConfig;
  const operatorResult = typeObj[`get${opConf.method}`]();
  const operator = opConf.getOperator?.(operatorResult) ?? opConf.operator!;
  if (operator === 'between') {
    return {
      operator,
      values: [operatorResult.getFrom(), operatorResult.getTo()].map(convertFromDb) as [any, any],
    }
  }
  if (operator === 'in' || operator === 'notin') {
    return {
      operator: operator,
      values: operatorResult.getValuesList().map(convertFromDb),
    }
  }
  if (operator === 'any' || operator === 'notset') {
    return {
      operator: operator,
    }
  }
  return {
    operator: operator as SegmentConditionOperatorSingle,
    value: convertFromDb(operatorResult.getValue()),
  };
}

function toConditionValue(obj: any): ConditionCriterionCommon {
  const tagConf = valuesTypes.find((t) => {
    // hasInteger | hasDate ...
    const hasMethodName = `has${t.method}`;
    return obj[hasMethodName]?.();
  }) as ValueTypeConfig;
  const typeObj = (obj as any)[`get${tagConf.method}`]();

  return {
    valueType: tagConf.tagType,
    ...parseOperators(typeObj, tagConf.convertFromDb ?? ((v: any) => v)),
  };
}

function getDaysAgoOperator<T extends { getOperator: () => number }>(
  daysAgo: T,
  DaysAgoOperator: any
): ConditionOperator {
  const daysAgoOperatorsMap: Record<any, ConditionOperator> = {
    [DaysAgoOperator.OPERATOR_EQ]: 'eq',
    [DaysAgoOperator.OPERATOR_NEQ]: 'noteq',
    [DaysAgoOperator.OPERATOR_LTE]: 'lte',
    [DaysAgoOperator.OPERATOR_GTE]: 'gte',
    [DaysAgoOperator.OPERATOR_BETWEEN]: 'between',
  };
  return daysAgoOperatorsMap[daysAgo.getOperator()];
}

function parseForNamedConditionCriterion(
  expression: any,
): NamedConditionCriterion {
  if (expression.hasDate()) {
    const DaysAgoOperator = expression.getDate()!.constructor.DaysAgo.Operator;
    if (expression.getDate()!.hasDaysAgo()) {
      const daysAgo = expression.getDate()!.getDaysAgo()!;
      const operator = getDaysAgoOperator(
        daysAgo,
        DaysAgoOperator,
      );
      return {
        name: expression.getName(),
        conditionCriterion: {
          valueType: 'date',
          condition: {
            variant: 'daysAgo',
            conditionClause: {
              operator,
              values: operator === 'between' ? daysAgo.getValuesList() as [any, any] : undefined,
              value: operator !== 'between' ? daysAgo.getValue() : undefined,
            },
          }
        },
      }
    } else {
      return {
        name: expression.getName(),
        conditionCriterion: {
          valueType: 'date',
          condition: {
            variant: 'datetime',
            conditionClause: parseOperators(expression.getDate()!, (t: Timestamp) => formatDateToUTCString(t.toDate())),
          }
        }
      };
    }
  } else {
    return {
      name: expression.getName(),
      conditionCriterion: toConditionValue(expression),
    };
  }
}


const eventCountOperatorsMap: Record<any, string> = {
  [EventCountOperator.OPERATOR_EQ]: 'eq',
  [EventCountOperator.OPERATOR_LTE]: 'lte',
  [EventCountOperator.OPERATOR_GTE]: 'gte',
};

function convertEventCountOperator(operator: any): ConditionOperator {
  return (eventCountOperatorsMap[operator] as ConditionOperator) ?? 'eq';
}

function convertEvent(
  event: FilterExpression.Event,
): SegmentConditionEvent {
  return {
    type: 'event',
    event: {
      name: event.getEvent(),
      count: {
        valueType: 'integer',
        operator: convertEventCountOperator(event.getCount()!.getOperator()),
        value: event.getCount()!.getValue(),
      } as SingleCondition,
      lastDays: event.getDate()!.getDaysAgo()!.getValue(),
      attributes: event.getAttributesList().map((attr) => {
        //return toConditionValue(attr, FilterExpression.Event.Attribute);
        return parseForNamedConditionCriterion(attr);
      }),
    },
  };
}

function getGroupOperator(op: OperationOperator): SegmentGroupOperator {
  const daysAgoOperatorsMap: Record<any, SegmentGroupOperator> = {
    [OperationOperator.OPERATOR_INTERSECT]: 'and',
    [OperationOperator.OPERATOR_UNION]: 'or',
    [OperationOperator.OPERATOR_DIFFERENCE]: 'and not',
  };
  return daysAgoOperatorsMap[op];
}

export function parseFilterExpression(expression: FilterExpression): SegmentCondition {
  if (expression.hasOperation()) {
    const operation = expression.getOperation()!;
    const operator = getGroupOperator(operation.getOperator());
    const conditions = operation.getOperandsList().map(parseFilterExpression);
    return {
      operator,
      conditions,
    };
  }
  if (expression.hasTag()) {
    const tag = expression.getTag()!;
    return {
      type: 'tag',
      ...parseForNamedConditionCriterion(tag),
    };
  }
  if (expression.hasTagDateMatch()) {
    const tagDateMatch = expression.getTagDateMatch()!;
    return {
      type: 'tag',
      name: tagDateMatch.getName(),
      conditionCriterion: {
        valueType: 'date',
        condition: {
          variant: 'dateMatch',
          value: {
            month: tagDateMatch.getMonth(),
            day: tagDateMatch.getDay(),
            addDays: tagDateMatch.getAddDays(),
          }
        },
      },
    };
  }
  if (expression.hasEvent()) {
    return convertEvent(expression.getEvent()!);
  }

  throw new Error(`Unknown expression kind: ${expression.getKindCase()}`);
}
